import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Clock,
  MapPin,
  X,
  ArrowRight,
  Ticket,
  Check,
  Users,
} from "lucide-react";
import { toast } from "react-toastify";

// Format currency symbol
const formatCurrency = (currency) => {
  if (!currency) return "$";
  const currencyUpper = currency.toUpperCase();
  if (currencyUpper === "USD") return "$";
  if (currencyUpper === "GHS") return "₵";
  return currency; // Return as-is if not recognized
};

export default function RegistrationDrawer({
  isOpen,
  onClose,
  event,
  eventDate,
  eventTime,
  isConference,
  selectedTicket,
  setSelectedTicket,
  onContinue,
}) {
  const handleClose = () => {
    setSelectedTicket(null);
    onClose();
  };

  const handleContinue = () => {
    if (selectedTicket) {
      if (onContinue) {
        onContinue(selectedTicket);
      } else {
        toast.success(
          `Selected ${selectedTicket.name} ticket. Proceeding to registration...`
        );
        handleClose();
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-50 backdrop-blur-sm bg-black/50"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="flex overflow-hidden fixed top-0 right-0 bottom-0 z-50 flex-col w-full max-w-md bg-white shadow-2xl"
          >
            <div className="flex sticky top-0 z-10 justify-between items-center p-6 bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg">
              <h2 className="text-2xl font-bold text-white">Select Ticket</h2>
              <motion.button
                onClick={handleClose}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 text-white rounded-full transition-colors hover:bg-white/20"
              >
                <X size={24} />
              </motion.button>
            </div>

            <div className="flex overflow-hidden flex-col flex-1 min-h-0">
              {/* Scrollable Content Area */}
              <div className="overflow-y-auto flex-1">
                {/* Event Info Summary - Compact */}
                <div className="flex-shrink-0 p-4 mx-6 mt-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                  <h3 className="mb-2 text-base font-bold text-gray-900 line-clamp-1">
                    {event?.event_title || "Event"}
                  </h3>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                    <div className="flex gap-1 items-center">
                      <Calendar size={14} className="text-emerald-600" />
                      <span>{eventDate}</span>
                    </div>
                    <div className="flex gap-1 items-center">
                      <Clock size={14} className="text-emerald-600" />
                      <span>{eventTime}</span>
                    </div>
                    <div className="flex gap-1 items-center">
                      <MapPin size={14} className="text-emerald-600" />
                      <span className="line-clamp-1">{event?.venue_name}</span>
                    </div>
                  </div>
                </div>

                {/* Ticket Options - Scrollable Compact List */}
                {isConference && event?.ticket_types?.length > 0 ? (
                  <div className="px-6 py-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                      Choose Your Ticket
                    </h3>
                    <span className="px-2 py-1 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full">
                      {event.ticket_types.length} options
                    </span>
                  </div>
                  <div className="space-y-2.5">
                    {event.ticket_types.map((ticket, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        onClick={() => setSelectedTicket(ticket)}
                        className={`relative p-3.5 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedTicket?.name === ticket.name
                            ? "border-emerald-500 bg-emerald-50 shadow-sm ring-1 ring-emerald-200"
                            : "border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/50"
                        }`}
                      >
                        {/* Selected Indicator */}
                        {selectedTicket?.name === ticket.name && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute top-2.5 right-2.5"
                          >
                            <div className="flex justify-center items-center w-5 h-5 text-white bg-emerald-500 rounded-full shadow-sm">
                              <Check size={12} />
                            </div>
                          </motion.div>
                        )}

                        {/* Compact Layout */}
                        <div className="flex gap-3 items-start pr-8">
                          {/* Price - Prominent */}
                          <div className="flex-shrink-0">
                            {ticket.price === 0 || parseFloat(ticket.price) === 0 ? (
                              <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-green-500 to-green-600 text-white text-sm font-bold rounded-lg shadow-md">
                                FREE
                              </div>
                            ) : (
                              <div className="text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-500">
                                {formatCurrency(ticket.currency)}
                                {ticket.price}
                              </div>
                            )}
                          </div>

                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex gap-2 items-center mb-1">
                              <h4 className="text-sm font-bold text-gray-900 line-clamp-1">
                                {ticket.name}
                              </h4>
                              {ticket.slots_allocated !== null && (
                                <div className="flex gap-0.5 items-center px-1.5 py-0.5 text-[10px] text-gray-600 bg-gray-100 rounded">
                                  <Users size={10} className="text-emerald-600" />
                                  <span>{ticket.slots_allocated}</span>
                                </div>
                              )}
                            </div>
                            {ticket.description && (
                              <p className="text-xs leading-snug text-gray-600 line-clamp-1 mb-1.5">
                                {ticket.description}
                              </p>
                            )}
                            {/* What's Included - Compact Tags */}
                            {ticket.whats_included?.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {ticket.whats_included.slice(0, 2).map((item, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex gap-0.5 items-center px-1.5 py-0.5 text-[10px] text-emerald-700 bg-emerald-50 rounded"
                                  >
                                    <Check size={8} className="text-emerald-600" />
                                    <span className="line-clamp-1 max-w-[100px]">{item}</span>
                                  </span>
                                ))}
                                {ticket.whats_included.length > 2 && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] text-gray-600 bg-gray-100 rounded">
                                    +{ticket.whats_included.length - 2}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  </div>
                ) : (
                  <div className="flex justify-center items-center p-8">
                    <div className="text-center">
                      <Ticket size={48} className="mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-600">
                        {isConference
                          ? "No ticket options available"
                          : "This is a trade mission. Registration details will be provided."}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Continue Button - Sticky Footer */}
              {selectedTicket && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex-shrink-0 p-6 bg-white border-t-2 border-gray-200 shadow-lg"
                >
                  <div className="p-3 mb-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-medium text-gray-700">
                        Selected:
                      </span>
                      <span className="text-base font-bold text-emerald-600">
                        {selectedTicket.price === 0 || parseFloat(selectedTicket.price) === 0
                          ? "FREE"
                          : `${formatCurrency(selectedTicket.currency)}${selectedTicket.price}`}
                      </span>
                    </div>
                    <p className="text-xs font-semibold text-gray-900">
                      {selectedTicket.name}
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleContinue}
                    className="flex gap-2 justify-center items-center px-6 py-3 w-full text-sm font-bold text-white bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-lg shadow-md transition-all hover:from-emerald-700 hover:to-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                  >
                    Continue
                    <ArrowRight size={18} />
                  </motion.button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

